import { HelpdeskAttachmentInput } from './HelpdeskAttachmentInput';
import { validateHelpdeskAttachments } from '../api/attachments';
import { useEffect, useState, type FormEvent } from 'react';
import { useAiReplyDraft } from '../model/use-ai-reply-draft';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Checkbox,
  Heading,
  Label,
  RequiredMark,
  Text,
  Textarea,
} from '@shared/ui';
import { z } from 'zod';
import type { HelpdeskEmailChannel } from '../api/email-delivery';
import { useRuntimeReply } from '../model/runtime-mutations';
const createHelpdeskReplySchema = z.object({
  message: z.string().trim().min(1).max(20000),
  kind: z.enum(['support-reply', 'internal-comment']),
});
import { useHelpdeskRuntime, type PresentationTask as HelpdeskTask } from '../model/runtime';

export interface HelpdeskReplyComposerViewProps {
  viewerAccessScope: string;
  taskUuid: string;
  emailChannel?: HelpdeskEmailChannel | null;
  kind?: 'support-reply' | 'internal-comment';
  showAcceptedReplies?: boolean;
  onSaved?: (task: HelpdeskTask) => void;
  activeMode?: string;
  active?: boolean;
}

/** b writes and displays locally accepted replies; d supplies the durable paginated history. */
export function HelpdeskReplyComposerView({
  viewerAccessScope,
  taskUuid,
  emailChannel,
  kind = 'support-reply',
  showAcceptedReplies = true,
  onSaved,
  activeMode = kind,
  active = true,
}: HelpdeskReplyComposerViewProps) {
  const { t } = useTranslation();
  const runtime = useHelpdeskRuntime();
  const client = useQueryClient();
  const reply = useRuntimeReply(taskUuid);
  const draftKey = `${viewerAccessScope}:${taskUuid}:${kind}`;
  const [acceptedDraftKey, setAcceptedDraftKey] = useState<string | null>(null);
  useEffect(() => {
    setAcceptedDraftKey(null);
  }, [draftKey]);
  const successMessage =
    kind === 'internal-comment'
      ? t('helpdesk.thread.commentSaved', 'Comment saved.')
      : t('helpdesk.commands.saved', 'Reply saved.');
  const [drafts, setDrafts] = useState<
    Partial<
      Record<string, { message: string; files: File[]; revision: number; emailChannelId?: string }>
    >
  >({});
  const draft = drafts[draftKey] ?? { message: '', files: [], revision: 0 };
  const { message, files } = draft;
  const emailEligible = runtime.audience === 'staff' && kind === 'support-reply';
  const emailRequested = emailEligible && Boolean(draft.emailChannelId);
  const emailAvailable = emailChannel?.available === true && Boolean(emailChannel.from);
  const emailBlocked =
    emailRequested && (!emailAvailable || draft.emailChannelId !== emailChannel.id);
  const changeDraft = (patch: Partial<typeof draft>) => {
    setAcceptedDraftKey(null);
    setDrafts((current) => {
      const previous = current[draftKey] ?? { message: '', files: [], revision: 0 };
      return { ...current, [draftKey]: { ...previous, ...patch, revision: previous.revision + 1 } };
    });
  };
  const setMessage = (message: string) => {
    changeDraft({ message });
  };
  const setFiles = (files: File[]) => {
    changeDraft({ files });
  };
  const ai = useAiReplyDraft({
    scope: viewerAccessScope,
    taskUuid,
    observation: JSON.stringify([draftKey, draft.revision, activeMode, active]),
    draft,
    enabled:
      runtime.audience === 'staff' &&
      kind === 'support-reply' &&
      activeMode === kind &&
      active &&
      !reply.isPending,
    apply: (result) => {
      changeDraft({ message: result.output.text });
    },
  });
  const [invalid, setInvalid] = useState(false);
  const messageId = `helpdesk-${kind}-${taskUuid}-message`;
  const invalidId = `${messageId}-invalid`;
  const acceptedKey = ['helpdesk', viewerAccessScope, 'session-replies', taskUuid] as const;
  const accepted = useQuery({
    queryKey: acceptedKey,
    queryFn: () => Promise.resolve<HelpdeskTask[]>([]),
    initialData: [],
    enabled: false,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!active) return;
    const result = createHelpdeskReplySchema.safeParse({ message, kind });
    setInvalid(!result.success);
    if (!result.success || !validateHelpdeskAttachments(files) || emailBlocked) return;
    reply.mutate(
      {
        ...result.data,
        files,
        ...(emailRequested && draft.emailChannelId
          ? { delivery: { channelId: draft.emailChannelId } }
          : {}),
      },
      {
        onSuccess: (response) => {
          client.setQueryData<HelpdeskTask[]>(acceptedKey, (current = []) =>
            current.some((item) => item.id === response.data.id)
              ? current
              : [...current, response.data]
          );
          setDrafts((current) => ({
            ...current,
            [draftKey]: {
              message: '',
              files: [],
              revision: (current[draftKey]?.revision ?? 0) + 1,
            },
          }));
          if (runtime.audience === 'staff')
            toast.success(emailRequested ? t('helpdesk.emailDelivery.queued') : successMessage);
          else setAcceptedDraftKey(draftKey);
          onSaved?.(response.data);
        },
      }
    );
  };
  return (
    <div className="space-y-4">
      {showAcceptedReplies ? (
        <Text size="sm" color="muted">
          {t('helpdesk.commands.sessionReplies', 'Replies saved during this session appear below.')}
        </Text>
      ) : null}
      {showAcceptedReplies
        ? accepted.data.map((item) => (
            <Card key={item.id}>
              <CardContent className="space-y-2 pt-4">
                <Text className="whitespace-pre-wrap break-words">{item.description}</Text>
                <Text size="sm" color="muted" className="text-right">
                  {item.author?.displayName ?? t('helpdesk.commands.staff', 'Staff')}
                </Text>
              </CardContent>
            </Card>
          ))
        : null}
      {runtime.audience === 'requester' && reply.isSuccess && acceptedDraftKey === draftKey ? (
        <Alert role="status">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      ) : null}
      <Card className="border-0 shadow-none">
        <CardContent className="space-y-4 p-0">
          {showAcceptedReplies ? (
            <Heading as="h2" size="lg">
              {kind === 'internal-comment'
                ? t('helpdesk.thread.internalComment', 'Internal comment')
                : t('helpdesk.commands.reply', 'Reply')}
            </Heading>
          ) : null}
          <Text size="sm" color="fieldLabel">
            {kind === 'internal-comment'
              ? t('helpdesk.thread.internalAudience', 'Only permitted staff can read this comment.')
              : t(
                  runtime.audience === 'requester'
                    ? 'helpdesk.widget.replyAudience'
                    : 'helpdesk.commands.replyAudience',
                  runtime.audience === 'requester'
                    ? 'Your message will be sent to support.'
                    : 'This reply will be visible to the requester.'
                )}
          </Text>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor={messageId}>
                  {kind === 'internal-comment'
                    ? t('helpdesk.thread.internalMessage', 'Internal message')
                    : t('helpdesk.commands.message', 'Message')}
                  <RequiredMark />
                </Label>
                {runtime.audience === 'staff' && kind === 'support-reply' ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={ai.pending || reply.isPending || !active || activeMode !== kind}
                    onClick={() => {
                      void ai.generate();
                    }}
                  >
                    <Sparkles className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                    {t(
                      ai.pending
                        ? 'helpdesk.aiReply.generating'
                        : ai.recovery
                          ? 'helpdesk.aiReply.recover'
                          : 'helpdesk.aiReply.generate'
                    )}
                  </Button>
                ) : null}
              </div>
              <Textarea
                id={messageId}
                value={message}
                maxLength={20000}
                required
                rows={5}
                disabled={reply.isPending || !active}
                aria-invalid={invalid}
                aria-describedby={invalid ? invalidId : undefined}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setInvalid(false);
                }}
              />
            </div>
            {ai.error ? (
              <Alert variant="destructive">
                <AlertTitle>{t(`helpdesk.aiReply.${ai.error}`)}</AlertTitle>
              </Alert>
            ) : null}
            {ai.suggestion ? (
              <section className="space-y-3" aria-labelledby={`${messageId}-suggestion-title`}>
                <Alert role="status">
                  <Info aria-hidden="true" className="size-4" />
                  <AlertTitle>
                    {t(
                      ai.suggestion.output.currentAtCompletion
                        ? 'helpdesk.aiReply.reviewTitle'
                        : 'helpdesk.aiReply.staleTitle'
                    )}
                  </AlertTitle>
                  <AlertDescription>
                    {t(
                      ai.suggestion.output.currentAtCompletion
                        ? `helpdesk.aiReply.${ai.reviewReason ?? 'review'}`
                        : 'helpdesk.aiReply.stale'
                    )}
                  </AlertDescription>
                </Alert>
                <Card>
                  <CardHeader>
                    <Heading as="h3" size="md" id={`${messageId}-suggestion-title`}>
                      {t('helpdesk.aiReply.suggestion')}
                    </Heading>
                  </CardHeader>
                  <CardContent>
                    <Text className="whitespace-pre-wrap break-words">
                      {ai.suggestion.output.text}
                    </Text>
                  </CardContent>
                  <CardFooter className="flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      density="compact"
                      className="max-w-full whitespace-normal"
                      onClick={ai.dismissSuggestion}
                    >
                      {t('helpdesk.aiReply.dismiss')}
                    </Button>
                    {ai.suggestion.output.currentAtCompletion ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        density="compact"
                        className="max-w-full whitespace-normal"
                        disabled={reply.isPending || !active || activeMode !== kind}
                        onClick={ai.applySuggestion}
                      >
                        {t('helpdesk.aiReply.useSuggestion')}
                      </Button>
                    ) : null}
                  </CardFooter>
                </Card>
              </section>
            ) : null}
            {emailEligible && emailChannel !== undefined ? (
              <div className="space-y-2">
                {emailChannel?.from ? (
                  <Text size="sm" color="muted">
                    {t('helpdesk.emailDelivery.from', { sender: emailChannel.from })}
                  </Text>
                ) : null}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${messageId}-email`}
                    checked={emailRequested}
                    disabled={reply.isPending || !active || (!emailAvailable && !emailRequested)}
                    onCheckedChange={(checked) => {
                      changeDraft({
                        emailChannelId: checked === true ? emailChannel?.id : undefined,
                      });
                    }}
                  />
                  <Label htmlFor={`${messageId}-email`}>
                    {t('helpdesk.emailDelivery.alsoSend')}
                  </Label>
                </div>
                {!emailAvailable || emailBlocked ? (
                  <Text size="sm" color="muted">
                    {t('helpdesk.emailDelivery.unavailable')}
                  </Text>
                ) : null}
                <Text size="sm" color="muted">
                  {t('helpdesk.emailDelivery.choiceHint')}
                </Text>
              </div>
            ) : null}
            <HelpdeskAttachmentInput
              id={`${messageId}-files`}
              files={files}
              disabled={reply.isPending || !active}
              uploading={reply.isPending && files.length > 0}
              onChange={setFiles}
            />
            {invalid ? (
              <Text id={invalidId} role="alert" size="sm" color="destructive">
                {t(
                  'helpdesk.commands.invalid',
                  'Check the required fields and enter a non-empty message.'
                )}
              </Text>
            ) : null}
            {reply.error ? (
              <Alert variant="destructive">
                <AlertTitle>
                  {t(
                    'helpdesk.commands.saveFailed',
                    'Could not save. Your draft is still here; you can try again.'
                  )}
                </AlertTitle>
              </Alert>
            ) : null}
            <div className="flex justify-start">
              <Button
                type="submit"
                disabled={
                  reply.isPending ||
                  !active ||
                  emailBlocked ||
                  message.trim().length === 0 ||
                  !validateHelpdeskAttachments(files)
                }
              >
                {reply.isPending
                  ? t('helpdesk.commands.sending', 'Sending...')
                  : t('helpdesk.commands.send', 'Send')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
